import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../../services/api';
import { prepareEntryHtmlForWebView, handleEntryContentLinkClick } from '../../utils/entryLinks';
import './DiaryModal.css';

function DiaryModal({ entries: initialEntries, onClose, initialIndex = 0 }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const pageIndex = searchParams.get('page') ? parseInt(searchParams.get('page'), 10) : initialIndex;
  const [isVisible, setIsVisible] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(pageIndex || initialIndex);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [showDatePicker, setShowDatePicker] = useState(false);
  
  // Pagination state
  const [loadedEntries, setLoadedEntries] = useState([]);
  const [lastEvaluatedKey, setLastEvaluatedKey] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [totalEntries, setTotalEntries] = useState(0);
  const [datePickerEntries, setDatePickerEntries] = useState([]);
  const [loadingDatePickerEntries, setLoadingDatePickerEntries] = useState(false);

  // SessionStorage keys for persistence (same as Dashboard)
  const STORAGE_KEYS = {
    loadedEntries: 'dashboard_loaded_entries',
    lastEvaluatedKey: 'dashboard_last_evaluated_key',
    hasMore: 'dashboard_has_more'
  };

  // Load more entries from API - defined early so it can be used in initialization
  const loadMoreEntries = useCallback(async () => {
    if (loadingMore || (!loading && !hasMore && loadedEntries.length > 0)) return;
    
    if (loadedEntries.length === 0) {
      setLoading(true);
    } else {
      setLoadingMore(true);
    }
    
    try {
      const options = {
        limit: 50,
        sort: '-timestamp',
      };
      
      if (lastEvaluatedKey) {
        options.lastEvaluatedKey = lastEvaluatedKey;
      }
      
      const data = await api.entries.getEntries(options);
      const formattedEntries = api.formatEntriesForDisplay(data.entries);
      
      if (loadedEntries.length > 0 && formattedEntries.length > 0) {
        const existingIds = new Set(loadedEntries.map(e => e.id || e.entryId));
        const newEntries = formattedEntries.filter(e => !existingIds.has(e.id || e.entryId));
        
        if (newEntries.length === 0) {
          setHasMore(false);
        } else {
          const updatedEntries = [...loadedEntries, ...newEntries];
          setLoadedEntries(updatedEntries);
          setLastEvaluatedKey(data.pagination?.lastEvaluatedKey || null);
          
          // Update hasMore - use totalEntries if available, otherwise use API response
          let finalHasMore = data.pagination?.hasMore || false;
          if (totalEntries > 0) {
            finalHasMore = updatedEntries.length < totalEntries;
          }
          setHasMore(finalHasMore);
          
          // Update sessionStorage (same as Dashboard)
          try {
            sessionStorage.setItem(STORAGE_KEYS.loadedEntries, JSON.stringify(updatedEntries));
            if (data.pagination?.lastEvaluatedKey) {
              sessionStorage.setItem(STORAGE_KEYS.lastEvaluatedKey, JSON.stringify(data.pagination.lastEvaluatedKey));
            } else {
              sessionStorage.removeItem(STORAGE_KEYS.lastEvaluatedKey);
            }
            sessionStorage.setItem(STORAGE_KEYS.hasMore, String(finalHasMore));
          } catch (error) {
            console.error('Error saving to sessionStorage:', error);
          }
        }
      } else {
        const updatedEntries = [...loadedEntries, ...formattedEntries];
        setLoadedEntries(updatedEntries);
        setLastEvaluatedKey(data.pagination?.lastEvaluatedKey || null);
        
        // Update hasMore - use totalEntries if available, otherwise use API response
        let finalHasMore = data.pagination?.hasMore || false;
        if (totalEntries > 0) {
          finalHasMore = updatedEntries.length < totalEntries;
        }
        setHasMore(finalHasMore);
        
        // Update sessionStorage
        try {
          sessionStorage.setItem(STORAGE_KEYS.loadedEntries, JSON.stringify(updatedEntries));
          if (data.pagination?.lastEvaluatedKey) {
            sessionStorage.setItem(STORAGE_KEYS.lastEvaluatedKey, JSON.stringify(data.pagination.lastEvaluatedKey));
          } else {
            sessionStorage.removeItem(STORAGE_KEYS.lastEvaluatedKey);
          }
          sessionStorage.setItem(STORAGE_KEYS.hasMore, String(finalHasMore));
        } catch (error) {
          console.error('Error saving to sessionStorage:', error);
        }
      }
    } catch (err) {
      console.error('Failed to load entries:', err);
      setHasMore(false);
    } finally {
      setLoadingMore(false);
      setLoading(false);
    }
  }, [lastEvaluatedKey, hasMore, loadingMore, loading, loadedEntries, totalEntries]);

  // Load total entry count from stats API
  useEffect(() => {
    const loadTotalCount = async () => {
      try {
        const response = await api.entries.getStats();
        if (response && response.stats) {
          setTotalEntries(response.stats.totalEntries || 0);
        }
      } catch (error) {
        console.error('Error loading stats:', error);
        // Fallback to loaded entries length if stats API fails
        setTotalEntries(loadedEntries.length);
      }
    };
    
    if (api.isAuthenticated()) {
      loadTotalCount();
    }
  }, [loadedEntries.length]);

  // Initialize with entries from sessionStorage or initial entries
  useEffect(() => {
    // Try to restore from sessionStorage first (same as Dashboard)
    try {
      const savedEntries = sessionStorage.getItem(STORAGE_KEYS.loadedEntries);
      if (savedEntries) {
        const parsedEntries = JSON.parse(savedEntries);
        if (Array.isArray(parsedEntries) && parsedEntries.length > 0) {
          setLoadedEntries(parsedEntries);
          
          const savedLastKey = sessionStorage.getItem(STORAGE_KEYS.lastEvaluatedKey);
          if (savedLastKey) {
            const parsedKey = JSON.parse(savedLastKey);
            setLastEvaluatedKey(parsedKey);
          }
          
          const savedHasMore = sessionStorage.getItem(STORAGE_KEYS.hasMore);
          if (savedHasMore !== null) {
            setHasMore(savedHasMore === 'true');
          }
          
          setTimeout(() => setIsVisible(true), 10);
          return;
        }
      }
    } catch (error) {
      console.error('Error restoring from sessionStorage:', error);
    }
    
    // Fallback to initial entries if no saved state
    if (initialEntries && initialEntries.length > 0) {
      const formatted = api.formatEntriesForDisplay(initialEntries);
      setLoadedEntries(formatted);
      setHasMore(initialEntries.length >= 50);
    } else {
      setLoading(true);
      loadMoreEntries();
    }
    setTimeout(() => setIsVisible(true), 10);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update currentIndex when page query parameter changes
  // Also load more entries if the index is beyond what we have loaded
  useEffect(() => {
    const pageParam = searchParams.get('page');
    if (pageParam !== null) {
      const index = parseInt(pageParam, 10);
      if (!isNaN(index) && index >= 0) {
        setCurrentIndex(index);
        
        // If the index is beyond loaded entries, load more
        if (index >= loadedEntries.length && hasMore && !loading && !loadingMore) {
          loadMoreEntries();
        }
      }
    }
  }, [searchParams, loadedEntries.length, hasMore, loading, loadingMore, loadMoreEntries]);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleClose = () => {
    if (onClose) {
      setIsVisible(false);
      setTimeout(onClose, 300);
    } else {
      navigate('/');
    }
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget && onClose) {
      handleClose();
    }
  };

  const goToNext = () => {
    const entries = loadedEntries;
    
    // Load more entries if we're approaching the end
    if (hasMore && currentIndex + 5 >= entries.length && !loadingMore) {
      loadMoreEntries();
    }
    
    if (currentIndex < entries.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  const goToPrev = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowRight') goToNext();
      if (e.key === 'ArrowLeft') goToPrev();
      if (e.key === 'Escape' && onClose) handleClose();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIndex]);

  const moods = [
    { 
      value: 'calm', 
      label: 'Calm', 
      color: '#3B82F6',
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10"/>
          <path d="M8 15s1.5-1 4-1 4 1 4 1"/>
          <line x1="9" y1="9" x2="9.01" y2="9"/>
          <line x1="15" y1="9" x2="15.01" y2="9"/>
        </svg>
      )
    },
    { 
      value: 'happy', 
      label: 'Happy', 
      color: '#10B981',
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10"/>
          <path d="M8 14s1.5 2 4 2 4-2 4-2"/>
          <line x1="9" y1="9" x2="9.01" y2="9"/>
          <line x1="15" y1="9" x2="15.01" y2="9"/>
        </svg>
      )
    },
    { 
      value: 'neutral', 
      label: 'Neutral', 
      color: '#6B7280',
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10"/>
          <line x1="8" y1="15" x2="16" y2="15"/>
          <line x1="9" y1="9" x2="9.01" y2="9"/>
          <line x1="15" y1="9" x2="15.01" y2="9"/>
        </svg>
      )
    },
    { 
      value: 'anxious', 
      label: 'Anxious', 
      color: '#F59E0B',
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10"/>
          <path d="M8 15s1 -1 4 -1 4 1 4 1"/>
          <path d="M9 10c0-1 .5-1.5 1-1.5s1 .5 1 1.5"/>
          <path d="M15 10c0-1-.5-1.5-1-1.5s-1 .5-1 1.5"/>
        </svg>
      )
    },
    { 
      value: 'sad', 
      label: 'Sad', 
      color: '#8B5CF6',
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10"/>
          <path d="M8 17s1.5-2 4-2 4 2 4 2"/>
          <line x1="9" y1="9" x2="9.01" y2="9"/>
          <line x1="15" y1="9" x2="15.01" y2="9"/>
        </svg>
      )
    },
    { 
      value: 'overwhelmed', 
      label: 'Overwhelmed', 
      color: '#EF4444',
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10"/>
          <circle cx="9" cy="10" r="1.5"/>
          <circle cx="15" cy="10" r="1.5"/>
          <path d="M9 16a3 3 0 0 0 6 0"/>
        </svg>
      )
    }
  ];

  const getMoodData = (mood) => {
    const moodData = moods.find(m => m.value === mood) || moods.find(m => m.value === 'neutral');
    return {
      label: moodData.label,
      color: moodData.color,
      icon: moodData.icon
    };
  };

  const handleDateSelect = (index) => {
    setCurrentIndex(index);
    setShowDatePicker(false);
  };

  // Load all entries for date picker when it opens
  useEffect(() => {
    if (showDatePicker) {
      // Start with loaded entries
      setDatePickerEntries(loadedEntries);
      
      // If we have more entries to load and haven't loaded them all, load them
      if (hasMore && loadedEntries.length < totalEntries && totalEntries > 0) {
        const loadAllDatePickerEntries = async () => {
          setLoadingDatePickerEntries(true);
          let allEntries = [...loadedEntries];
          let currentLastKey = lastEvaluatedKey;
          let stillHasMore = hasMore;
          
          try {
            // Keep loading until we have all entries or API says no more
            while (stillHasMore && allEntries.length < totalEntries) {
              const options = {
                limit: 200, // Load larger batches for date picker
                sort: '-timestamp',
              };
              
              if (currentLastKey) {
                options.lastEvaluatedKey = currentLastKey;
              }
              
              const data = await api.entries.getEntries(options);
              const formattedEntries = api.formatEntriesForDisplay(data.entries);
              
              if (formattedEntries.length === 0) {
                stillHasMore = false;
                break;
              }
              
              // Filter out duplicates
              const existingIds = new Set(allEntries.map(e => e.id || e.entryId));
              const newEntries = formattedEntries.filter(e => !existingIds.has(e.id || e.entryId));
              
              if (newEntries.length === 0) {
                stillHasMore = false;
                break;
              }
              
              allEntries = [...allEntries, ...newEntries];
              currentLastKey = data.pagination?.lastEvaluatedKey || null;
              stillHasMore = data.pagination?.hasMore || false;
              
              // Update state progressively so user sees entries loading
              setDatePickerEntries([...allEntries]);
              
              // Stop if we've reached the total
              if (totalEntries > 0 && allEntries.length >= totalEntries) {
                stillHasMore = false;
                break;
              }
              
              // Safety limit - don't load more than 2000 entries
              if (allEntries.length >= 2000) {
                stillHasMore = false;
                break;
              }
            }
          } catch (error) {
            console.error('Error loading date picker entries:', error);
          } finally {
            setLoadingDatePickerEntries(false);
          }
        };
        
        loadAllDatePickerEntries();
      }
    }
  }, [showDatePicker, loadedEntries, hasMore, totalEntries, lastEvaluatedKey]);

  const entries = loadedEntries;
  const currentEntry = entries[currentIndex];
  
  if (entries.length === 0 && loading) {
    return (
      <div className={`diary-modal ${isVisible ? 'diary-modal--visible' : ''} ${!onClose ? 'diary-modal--page' : ''}`}>
        <div className="diary-modal__container">
          <div className="diary-modal__loading">Loading diary...</div>
        </div>
      </div>
    );
  }
  
  if (!currentEntry) return null;

  const entryDate = new Date(currentEntry.timestamp);
  const formattedDate = entryDate.toLocaleDateString('en-US', { 
    weekday: 'long',
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  });
  const moodData = getMoodData(currentEntry.mood);
  const htmlContent = currentEntry.text || '';

  // Helper to convert hex to rgba
  const hexToRgba = (hex, alpha) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result 
      ? `rgba(${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}, ${alpha})`
      : `rgba(112, 119, 161, ${alpha})`;
  };

  return (
    <div 
      className={`diary-modal ${isVisible ? 'diary-modal--visible' : ''} ${!onClose ? 'diary-modal--page' : ''}`}
      onClick={handleBackdropClick}
    >
      <div className="diary-modal__container">
        {/* Close Button */}
        <button 
          className="diary-modal__close"
          onClick={handleClose}
          aria-label={onClose ? "Close diary" : "Back to home"}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            {onClose ? (
              <>
                <line x1="18" y1="6" x2="6" y2="18"/>
                <line x1="6" y1="6" x2="18" y2="18"/>
              </>
            ) : (
              <path d="M19 12H5M12 19l-7-7 7-7"/>
            )}
          </svg>
        </button>

        {/* Date Picker Button */}
        <button 
          className="diary-modal__date-picker-btn"
          onClick={() => setShowDatePicker(!showDatePicker)}
          aria-label="Jump to date"
          title="Jump to a specific entry"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
            <line x1="16" y1="2" x2="16" y2="6"/>
            <line x1="8" y1="2" x2="8" y2="6"/>
            <line x1="3" y1="10" x2="21" y2="10"/>
          </svg>
        </button>

        {/* Date Picker Panel */}
        {showDatePicker && (
          <>
            <div 
              className="diary-modal__date-picker-overlay" 
              onClick={() => setShowDatePicker(false)}
            />
            <div className="diary-modal__date-picker">
              <div className="diary-modal__date-picker-header">
                <h3>Jump to Entry {datePickerEntries.length > 0 && `(${datePickerEntries.length}${totalEntries > datePickerEntries.length ? ` of ${totalEntries}` : ''})`}</h3>
                <button 
                  className="diary-modal__date-picker-close"
                  onClick={() => setShowDatePicker(false)}
                  aria-label="Close"
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18"/>
                    <line x1="6" y1="6" x2="18" y2="18"/>
                  </svg>
                </button>
              </div>
              <div className="diary-modal__date-picker-list">
                {datePickerEntries.map((entry, datePickerIndex) => {
                  // Find the index in loadedEntries to navigate correctly
                  const entryIndexInLoaded = loadedEntries.findIndex(e => (e.id || e.entryId) === (entry.id || entry.entryId));
                  const isCurrentPageDate = entryIndexInLoaded === currentIndex;
                  const entryDate = new Date(entry.timestamp);
                  const moodData = getMoodData(entry.mood);
                  
                  return (
                    <button
                      key={entry.id || entry.entryId}
                      className={`diary-modal__date-item ${isCurrentPageDate ? 'diary-modal__date-item--active' : ''}`}
                      onClick={() => {
                        // If entry is not in loadedEntries, we need to load it first
                        if (entryIndexInLoaded === -1) {
                          // Navigate to this index - DiaryModal will load more entries if needed
                          navigate(`/diary?page=${datePickerIndex}`);
                          setShowDatePicker(false);
                        } else {
                          handleDateSelect(entryIndexInLoaded);
                        }
                      }}
                    >
                      <div className="diary-modal__date-item-date">
                        <span className="diary-modal__date-item-day">
                          {entryDate.toLocaleDateString('en-US', { day: 'numeric' })}
                        </span>
                        <span className="diary-modal__date-item-month">
                          {entryDate.toLocaleDateString('en-US', { month: 'short' })}
                        </span>
                      </div>
                      <div className="diary-modal__date-item-info">
                        <span className="diary-modal__date-item-weekday">
                          {entryDate.toLocaleDateString('en-US', { weekday: 'long' })}
                        </span>
                        <div className="diary-modal__date-item-mood">
                          <span className="diary-modal__date-item-mood-icon" style={{ color: moodData.color }}>
                            {moodData.icon}
                          </span>
                          <span style={{ color: moodData.color }}>{moodData.label}</span>
                        </div>
                      </div>
                    </button>
                  );
                })                }
                {loadingDatePickerEntries && (
                  <div style={{ padding: 'var(--space-lg)', textAlign: 'center', color: 'var(--color-muted-slate)' }}>
                    Loading more entries...
                  </div>
                )}
                {!hasMore && datePickerEntries.length < totalEntries && totalEntries > 0 && (
                  <div style={{ padding: 'var(--space-lg)', textAlign: 'center', color: 'var(--color-muted-slate)', fontSize: '0.875rem' }}>
                    Showing {datePickerEntries.length} of {totalEntries} entries
                  </div>
                )}
              </div>
            </div>
          </>
        )}

        {/* Main Card */}
        <div className="diary-modal__card">
          {/* Header */}
          <div className="diary-modal__header">
            <div className="diary-modal__header-content">
              <div className="diary-modal__date">{formattedDate}</div>
              <div 
                className="diary-modal__mood-badge" 
                style={{ 
                  borderColor: moodData.color,
                  color: moodData.color,
                  backgroundColor: hexToRgba(moodData.color, 0.1)
                }}
              >
                <span className="diary-modal__mood-icon" style={{ color: moodData.color }}>
                  {moodData.icon}
                </span>
                <span className="diary-modal__mood-label" style={{ color: moodData.color }}>
                  {moodData.label}
                </span>
              </div>
            </div>
            <div className="diary-modal__counter">
              {currentIndex + 1} of {totalEntries || entries.length}{hasMore ? '+' : ''}
            </div>
          </div>

          {/* Content */}
          <div className="diary-modal__content">
            <div 
              className="diary-modal__entry-content"
              dangerouslySetInnerHTML={{ __html: prepareEntryHtmlForWebView(htmlContent) }}
              onClick={handleEntryContentLinkClick}
              role="article"
            />
          </div>
        </div>

        {/* Navigation Buttons */}
        <button 
          className="diary-modal__nav diary-modal__nav--prev"
          onClick={goToPrev}
          disabled={currentIndex === 0}
          aria-label="Previous entry"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6"/>
          </svg>
        </button>

        <button 
          className="diary-modal__nav diary-modal__nav--next"
          onClick={goToNext}
          disabled={currentIndex >= entries.length - 1 || loadingMore}
          aria-label="Next entry"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="9 18 15 12 9 6"/>
          </svg>
        </button>
      </div>
    </div>
  );
}

export default DiaryModal;
